import { create } from "zustand";
import { fetchDocumentPresignedUrl } from "@/utils/hooks/useDocumentUpload";

/**
 * Cached presigned URL entry
 */
interface CachedUrl {
  url: string;
  fileName: string;
  timestamp: number;
}

/**
 * Presigned URL cache store state
 */
interface PresignedUrlState {
  /**
   * Cache of presigned URLs keyed by documentId
   */
  cache: Record<string, CachedUrl>;

  /**
   * Get a presigned URL for a document
   * - Returns cached URL if available and not expired
   * - Otherwise fetches fresh URL from backend and caches it
   *
   * @param documentId - The document ID to get URL for
   * @param forceRefresh - Force fetching fresh URL from backend
   * @returns Promise resolving to { presignedUrl, fileName }
   */
  getPresignedUrl: (
    documentId: string,
    forceRefresh?: boolean
  ) => Promise<{ presignedUrl: string; fileName: string }>;

  /**
   * Clear cached URL for a specific document
   *
   * @param documentId - The document ID to clear cache for
   */
  clearCache: (documentId: string) => void;

  /**
   * Clear all cached URLs
   */
  clearAllCache: () => void;

  /**
   * Check if a document has a cached URL that's still valid
   *
   * @param documentId - The document ID to check
   * @returns true if cached and not expired
   */
  isCached: (documentId: string) => boolean;
}

/**
 * Cache TTL in milliseconds (15 minutes)
 * Presigned URLs from S3 typically expire after 1 hour,
 * so 15 minutes provides a safe buffer while reducing backend requests
 */
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Check if a cached entry is expired
 */
const isExpired = (cached: CachedUrl): boolean => {
  return Date.now() - cached.timestamp > CACHE_TTL_MS;
};

/**
 * Presigned URL cache store
 *
 * Centralized store for managing presigned URLs with automatic caching.
 * Reduces backend requests by caching URLs for 15 minutes.
 *
 * @example
 * ```typescript
 * const { getPresignedUrl } = usePresignedUrlStore();
 *
 * // Get URL (uses cache if available)
 * const { presignedUrl, fileName } = await getPresignedUrl(documentId);
 *
 * // Force fresh fetch
 * const { presignedUrl } = await getPresignedUrl(documentId, true);
 * ```
 */
export const usePresignedUrlStore = create<PresignedUrlState>((set, get) => ({
  cache: {},

  getPresignedUrl: async (documentId: string, forceRefresh = false) => {
    const { cache } = get();
    const cached = cache[documentId];

    // Return cached URL if valid and not forcing refresh
    if (!forceRefresh && cached && !isExpired(cached)) {
      console.log(`✅ Using cached presigned URL for document ${documentId}`);
      return {
        presignedUrl: cached.url,
        fileName: cached.fileName,
      };
    }

    // Fetch fresh URL from backend
    console.log(`🔄 Fetching fresh presigned URL for document ${documentId}`);
    try {
      const { presignedUrl, fileName } =
        await fetchDocumentPresignedUrl(documentId);

      // Cache the fresh URL
      set((state) => ({
        cache: {
          ...state.cache,
          [documentId]: {
            url: presignedUrl,
            fileName,
            timestamp: Date.now(),
          },
        },
      }));

      return { presignedUrl, fileName };
    } catch (error) {
      console.error(
        `❌ Failed to fetch presigned URL for document ${documentId}:`,
        error
      );
      throw error;
    }
  },

  clearCache: (documentId: string) => {
    set((state) => {
      const newCache = { ...state.cache };
      delete newCache[documentId];
      return { cache: newCache };
    });
  },

  clearAllCache: () => {
    set({ cache: {} });
  },

  isCached: (documentId: string) => {
    const { cache } = get();
    const cached = cache[documentId];
    return cached !== undefined && !isExpired(cached);
  },
}));
